export type TreasuryQuotePoint = {
  date: string;
  buyRate: number | null;
  sellRate: number | null;
  buyPu: number | null;
  sellPu: number | null;
};

export type TreasurySeries = 'buyRate' | 'sellRate' | 'buyPu' | 'sellPu';

export function defaultTreasurySeries(series: TreasurySeries[], defaultEnabled: TreasurySeries[] = series) {
  const defaults = new Set(defaultEnabled);
  return Object.fromEntries(
    (['buyRate', 'sellRate', 'buyPu', 'sellPu'] as TreasurySeries[]).map((key) => [
      key,
      series.includes(key) && defaults.has(key),
    ]),
  ) as Record<TreasurySeries, boolean>;
}

export function visibleTreasurySeries(series: TreasurySeries[], enabled: Partial<Record<TreasurySeries, boolean>>) {
  return series.filter((key) => enabled[key] !== false);
}

export function treasuryChartHasData(
  points: TreasuryQuotePoint[],
  series: TreasurySeries[],
  defaultEnabled: TreasurySeries[] = series,
) {
  return visibleTreasurySeries(series, defaultTreasurySeries(series, defaultEnabled)).some((key) =>
    points.some((point) => point[key] !== null && Number.isFinite(point[key])),
  );
}

export const periodDays: Record<string, number | null> = {
  '1M': 31,
  '3M': 92,
  '6M': 183,
  '1A': 365,
  '3A': 1095,
  TUDO: null,
};

const periodLabels: Record<string, string> = {
  '1M': 'Último mês',
  '3M': 'Últimos 3 meses',
  '6M': 'Últimos 6 meses',
  '1A': 'Último ano',
  '3A': 'Últimos 3 anos',
  TUDO: 'Todo o histórico',
};

export function filterTreasuryPeriod(points: TreasuryQuotePoint[], period: string) {
  const validPoints = points.filter((point) => parseTreasuryDate(point.date));
  const days = periodDays[period] ?? null;
  if (!days || validPoints.length === 0) return validPoints;
  const latest = validPoints.reduce((max, point) => (point.date > max ? point.date : max), validPoints[0].date);
  const end = parseTreasuryDate(latest);
  if (!end) return [];
  end.setUTCDate(end.getUTCDate() - days);
  const start = end.toISOString().slice(0, 10);
  return validPoints.filter((point) => point.date >= start && point.date <= latest);
}

export function seriesStats(points: TreasuryQuotePoint[], series: TreasurySeries) {
  const values = points
    .map((point) => point[series])
    .filter((value): value is number => value !== null && Number.isFinite(value));
  if (!values.length) return null;
  const first = values[0];
  const last = values[values.length - 1];
  return {
    min: Math.min(...values),
    max: Math.max(...values),
    average: values.reduce((sum, value) => sum + value, 0) / values.length,
    change: last - first,
  };
}

export function formatTreasuryDate(value: string | Date | null | undefined) {
  if (!value) return '—';
  const date = parseTreasuryDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'medium',
    timeZone: 'UTC',
  }).format(date);
}

export function treasuryPeriodContext(points: TreasuryQuotePoint[], period: string) {
  const validDates = points
    .map((point) => point.date)
    .filter((date) => parseTreasuryDate(date))
    .sort();
  const startDate = validDates[0] ?? null;
  const endDate = validDates[validDates.length - 1] ?? null;

  return {
    label: periodLabels[period] ?? period,
    startDate: formatTreasuryDate(startDate),
    endDate: formatTreasuryDate(endDate),
  };
}

function parseTreasuryDate(value: string | Date) {
  const date = typeof value === 'string' ? new Date(value.includes('T') ? value : `${value}T00:00:00Z`) : value;
  return Number.isNaN(date.getTime()) ? null : date;
}
